// ============================================
// DASHBOARD PAGE - REFACTORISÉ
// Structure simple, lisible, connectée aux vraies données
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Package, 
    Tags, 
    AlertTriangle, 
    TrendingUp, 
    ShoppingCart,
    DollarSign,
    Clock,
    XCircle,
    UtensilsCrossed,
    Users,
    Eye,
    Calendar,
    MessageSquare,
    MapPin,
    Wine,
    Warehouse,
    CreditCard,
    RefreshCw,
    ChefHat,
    GlassWater,
    CalendarCheck,
    Target,
    Trophy,
    Settings
} from 'lucide-react';
import { useEstablishment } from '../components/EstablishmentSelector';
import StockAlertBanner from '../components/StockAlertBanner';
import StaffPresenceModule from '../components/StaffPresenceModule';
import { GABON_TIMEZONE, getGabonTimeString } from '../utils/gabonTime';

// ============================================
// OBJECTIF CA PROGRESS CARD
// ============================================
const ObjectifCACard = ({ kpis, onSettingsClick }) => {
    const percentage = kpis?.objectif_pourcentage || 0;
    const isAchieved = kpis?.objectif_atteint || false;
    const objectif = kpis?.objectif_ca || 0;
    const ca = kpis?.ca_du_jour || 0;
    
    // Couleur basée sur le pourcentage
    const getProgressColor = () => {
        if (percentage >= 100) return 'bg-emerald-500';
        if (percentage >= 80) return 'bg-emerald-400';
        if (percentage >= 50) return 'bg-orange-400';
        return 'bg-red-400';
    };
    
    const getTextColor = () => {
        if (percentage >= 100) return 'text-emerald-600';
        if (percentage >= 80) return 'text-emerald-500';
        if (percentage >= 50) return 'text-orange-500';
        return 'text-red-500';
    };

    if (objectif === 0) {
        return (
            <Card className="border-0 shadow-md bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                <Target className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Objectif CA du jour</p>
                                <p className="text-xs text-gray-400">Aucun objectif défini</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={onSettingsClick}>
                            <Settings className="w-4 h-4 mr-1" />
                            Définir
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={`border-0 shadow-md ${isAchieved ? 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 ring-2 ring-emerald-500' : 'bg-white dark:bg-gray-900'}`}>
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isAchieved ? 'bg-emerald-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'}`}>
                            {isAchieved ? <Trophy className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Objectif CA du jour</p>
                            {kpis?.objectif_message && (
                                <p className="text-xs text-gray-500 italic">{kpis.objectif_message}</p>
                            )}
                        </div>
                    </div>
                    {isAchieved && (
                        <Badge className="bg-emerald-500 text-white animate-pulse">
                            Objectif atteint! 🎉
                        </Badge>
                    )}
                </div>
                
                {/* Progress bar */}
                <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                        <span className={`font-bold ${getTextColor()}`}>
                            {kpis?.ca_du_jour_formatted || '0 CFA'}
                        </span>
                        <span className="text-gray-500">
                            / {kpis?.objectif_ca_formatted || '0 CFA'}
                        </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                            className={`h-full ${getProgressColor()} transition-all duration-500 rounded-full`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <span className={`text-lg font-bold ${getTextColor()}`}>
                            {percentage}%
                        </span>
                        <span className="text-xs text-gray-500">
                            {percentage >= 100 
                                ? `+${Math.round(ca - objectif).toLocaleString('fr-FR')} CFA de bonus!` 
                                : `Reste ${Math.round(objectif - ca).toLocaleString('fr-FR')} CFA`
                            }
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// ============================================
// EMPLOYÉ DU MOIS CARD (Compact)
// ============================================
const EmployeeOfMonthCard = ({ employee }) => {
    if (!employee || !employee.name) return null;
    
    return (
        <Card className="border-0 shadow-md bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30">
            <CardContent className="p-3">
                <div className="flex items-center gap-3">
                    {/* Photo */}
                    <div className="relative">
                        {employee.photo_url ? (
                            <img 
                                src={employee.photo_url} 
                                alt={employee.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center border-2 border-amber-400">
                                <span className="text-lg font-bold text-amber-700 dark:text-amber-300">
                                    {employee.name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <Trophy className="w-3 h-3 text-white" />
                        </div>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">
                                Employé du mois
                            </Badge>
                        </div>
                        <p className="font-semibold text-gray-900 dark:text-white truncate">{employee.name}</p>
                        {employee.position && (
                            <p className="text-xs text-gray-500 truncate">{employee.position}</p>
                        )}
                    </div>
                </div>
                {employee.message && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 italic line-clamp-1">
                        "{employee.message}"
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// ============================================
// KPI CARD COMPONENT
// ============================================
const KPICard = ({ title, value, subtitle, icon: Icon, alert = false, trend = null, onClick = null }) => (
    <Card 
        className={`border-0 shadow-md hover:shadow-lg transition-all ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
        onClick={onClick}
    >
        <CardContent className="p-4 md:p-5">
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-xs md:text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
                    <p className={`text-xl md:text-2xl font-bold tracking-tight ${
                        alert ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                    }`}>
                        {value}
                    </p>
                    {subtitle && (
                        <p className="text-[10px] md:text-xs text-gray-500">{subtitle}</p>
                    )}
                </div>
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${
                    alert 
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                        : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                }`}>
                    <Icon className="w-5 h-5 md:w-6 md:h-6" />
                </div>
            </div>
            {trend && (
                <div className="mt-2 flex items-center gap-1 text-xs">
                    <TrendingUp className="w-3 h-3 text-emerald-500" />
                    <span className="text-emerald-600">{trend}</span>
                </div>
            )}
        </CardContent>
    </Card>
);

// ============================================
// QUICK ACTION BUTTON
// ============================================
const QuickActionButton = ({ to, icon: Icon, label, sublabel, colorClass }) => (
    <Link to={to}>
        <Card className={`border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] ${colorClass}`}>
            <CardContent className="p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h3 className="font-semibold text-white">{label}</h3>
                    <p className="text-xs text-white/70">{sublabel}</p>
                </div>
            </CardContent>
        </Card>
    </Link>
);

// ============================================
// EMPTY STATE MESSAGE
// ============================================
const EmptyState = ({ message, icon: Icon = Package }) => (
    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
        <Icon className="w-10 h-10 mb-2 opacity-50" />
        <p className="text-sm">{message}</p>
    </div>
);

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================
const DashboardPage = () => {
    const { api, user, loading: authLoading } = useAuth();
    const { selectedEstablishmentId } = useEstablishment();
    const navigate = useNavigate();
    
    const [kpis, setKpis] = useState(null);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [objectifNotified, setObjectifNotified] = useState(false);
    
    // ============================================
    // REDIRECTION AUTOMATIQUE SELON LE RÔLE
    // Évite le flicker en redirigeant les rôles opérationnels
    // ============================================
    useEffect(() => {
        // Attendre que l'auth soit complètement chargée
        if (authLoading || !user?.role) return;
        
        const role = user.role.toUpperCase();
        
        // Redirection vers le dashboard dédié selon le rôle
        const roleRedirects = {
            'SERVEUR': '/server-dashboard',
            'CUISINIER': '/kitchen',
            'BARMAN': '/bar-dashboard',
            'CAISSIER': '/pos',
            'CHEF_CUISINE': '/kitchen-dashboard'
        };
        
        if (roleRedirects[role]) {
            navigate(roleRedirects[role], { replace: true });
            return;
        }
    }, [user, authLoading, navigate]);
    
    const isAdmin = user?.role === 'ADMIN';
    const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);
    // Rôles qui peuvent voir les données financières (CA, achats, valeur stock, etc.)
    const canViewFinancials = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'].includes(user?.role);
    // Rôles qui ne doivent PAS voir les données financières
    const isRestrictedRole = ['CUISINIER', 'SERVEUR', 'BARMAN', 'EMPLOYE'].includes(user?.role);
    
    // Audio pour notification
    const playSuccessSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
            oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
            oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (e) {
            console.log('Audio not supported');
        }
    };

    // ============================================
    // FETCH DATA
    // ============================================
    const fetchDashboardData = useCallback(async () => {
        // Ne pas charger les données si c'est un rôle restreint (sera redirigé)
        if (isRestrictedRole) {
            setLoading(false);
            return;
        }
        
        try {
            const estParam = selectedEstablishmentId ? `?establishment_id=${selectedEstablishmentId}` : '';
            
            // Fetch all KPIs in parallel
            const [kpisRes, lowStockRes, activityRes] = await Promise.all([
                api().get(`/dashboard-kpi/main-stats${estParam}`).catch(() => null),
                api().get(`/dashboard-kpi/low-stock-products?limit=5`).catch(() => null),
                api().get(`/dashboard-kpi/recent-activity?limit=5`).catch(() => null)
            ]);
            
            if (kpisRes?.data) {
                const newKpis = kpisRes.data;
                
                // Notification si objectif atteint pour la première fois
                if (newKpis.objectif_atteint && !objectifNotified && newKpis.objectif_ca > 0) {
                    setObjectifNotified(true);
                    playSuccessSound();
                    toast.success(
                        <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <div>
                                <p className="font-bold">Objectif atteint! 🎉</p>
                                <p className="text-sm">{newKpis.ca_du_jour_formatted} / {newKpis.objectif_ca_formatted}</p>
                            </div>
                        </div>,
                        { duration: 10000 }
                    );
                }
                
                setKpis(newKpis);
                setLastUpdate(new Date());
            }
            setLowStockProducts(lowStockRes?.data || []);
            setRecentActivity(activityRes?.data || []);
            
        } catch (error) {
            console.error('Dashboard fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [api, selectedEstablishmentId, objectifNotified, isRestrictedRole]);

    // Initial load + auto-refresh every 30 seconds
    useEffect(() => {
        fetchDashboardData();
        const interval = setInterval(fetchDashboardData, 30000);
        return () => clearInterval(interval);
    }, [fetchDashboardData]);
    
    // ============================================
    // REDIRECTION DES RÔLES RESTREINTS
    // (Doit être APRÈS tous les hooks)
    // ============================================
    // Si c'est un rôle restreint qui doit être redirigé, afficher un loader neutre
    // Ceci évite le "flicker" où le dashboard admin apparaît brièvement
    if (isRestrictedRole) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background" data-testid="role-redirect-loader">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Chargement de votre espace...</p>
                </div>
            </div>
        );
    }

    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                        Tableau de Bord
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {lastUpdate ? `Mis à jour à ${lastUpdate.toLocaleTimeString('fr-FR', { timeZone: GABON_TIMEZONE })}` : 'Chargement...'}
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchDashboardData}
                    className="gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Actualiser
                </Button>
            </div>

            {/* Stock Alert Banner */}
            <StockAlertBanner />

            {/* ============================================ */}
            {/* OBJECTIF CA + EMPLOYÉ DU MOIS */}
            {/* Visible uniquement pour les rôles autorisés */}
            {/* ============================================ */}
            {canViewFinancials && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2">
                        <ObjectifCACard 
                            kpis={kpis} 
                            onSettingsClick={() => navigate('/motivation-management')}
                        />
                    </div>
                    <div>
                        <EmployeeOfMonthCard employee={kpis?.employee_of_month} />
                    </div>
                </div>
            )}

            {/* Employé du mois seul pour les rôles restreints */}
            {!canViewFinancials && kpis?.employee_of_month && (
                <EmployeeOfMonthCard employee={kpis?.employee_of_month} />
            )}

            {/* ============================================ */}
            {/* LIGNE 1 - KPIs BUSINESS */}
            {/* Données financières masquées pour CUISINIER, SERVEUR, BARMAN */}
            {/* ============================================ */}
            {canViewFinancials ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard
                        title="Chiffre d'affaires"
                        value={kpis?.ca_du_jour_formatted || '0 CFA'}
                        subtitle={kpis?.ventes_count > 0 ? `${kpis.ventes_count} vente(s) aujourd'hui` : "Aucune vente aujourd'hui"}
                        icon={DollarSign}
                        alert={kpis?.ca_du_jour === 0}
                    />
                    <KPICard
                        title="Achats du jour"
                        value={kpis?.achats_du_jour_formatted || '0 CFA'}
                        subtitle={kpis?.achats_count > 0 ? `${kpis.achats_count} achat(s)` : "Aucun achat aujourd'hui"}
                        icon={ShoppingCart}
                    />
                    <KPICard
                        title="Valeur du stock"
                        value={kpis?.valeur_stock_formatted || '0 CFA'}
                        subtitle={`${kpis?.total_products || 0} produits`}
                        icon={Warehouse}
                    />
                    <KPICard
                        title="Stock bas"
                        value={kpis?.stock_bas_count || 0}
                        subtitle={kpis?.stock_bas_count > 0 ? "Produits à réapprovisionner" : "Stock OK"}
                        icon={AlertTriangle}
                        alert={kpis?.stock_bas_count > 0}
                        onClick={() => navigate('/stock-alerts')}
                    />
                </div>
            ) : (
                /* Version allégée pour les rôles restreints - pas de données financières */
                <div className="grid grid-cols-2 gap-4">
                    <KPICard
                        title="Stock bas"
                        value={kpis?.stock_bas_count || 0}
                        subtitle={kpis?.stock_bas_count > 0 ? "Produits à réapprovisionner" : "Stock OK"}
                        icon={AlertTriangle}
                        alert={kpis?.stock_bas_count > 0}
                    />
                    <KPICard
                        title="Total produits"
                        value={kpis?.total_products || 0}
                        subtitle="Produits actifs"
                        icon={Package}
                    />
                </div>
            )}

            {/* ============================================ */}
            {/* LIGNE 2 - SUIVI PRODUITS */}
            {/* Visible uniquement pour les rôles avec accès produits */}
            {/* ============================================ */}
            {canViewFinancials && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard
                        title="Total produits"
                        value={kpis?.total_products || 0}
                        subtitle="Produits actifs"
                        icon={Package}
                        onClick={() => navigate('/products')}
                    />
                    <KPICard
                        title="Produits périmés"
                        value={kpis?.produits_perimes || 0}
                        subtitle={kpis?.produits_perimes > 0 ? "À retirer" : "Aucun produit périmé"}
                        icon={XCircle}
                        alert={kpis?.produits_perimes > 0}
                    />
                    <KPICard
                        title="Péremption proche"
                        value={kpis?.peremption_proche || 0}
                        subtitle="Dans les 7 jours"
                        icon={Clock}
                        alert={kpis?.peremption_proche > 0}
                    />
                    <KPICard
                        title="Catégories"
                        value={kpis?.total_categories || 0}
                        subtitle="Catégories produits"
                        icon={Tags}
                        onClick={() => navigate('/categories')}
                    />
                </div>
            )}

            {/* ============================================ */}
            {/* LIGNE 3 - OPÉRATIONNEL */}
            {/* ============================================ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="Caisses"
                    value={`${kpis?.caisses_ouvertes || 0}/${kpis?.total_caisses || 0}`}
                    subtitle={kpis?.caisses_ouvertes > 0 ? "Ouvertes" : "Aucune caisse ouverte"}
                    icon={CreditCard}
                    onClick={() => navigate('/pos')}
                />
                <KPICard
                    title="Tables"
                    value={`${kpis?.tables_occupees || 0}/${kpis?.total_tables || 0}`}
                    subtitle={`${kpis?.tables_libres || 0} libres`}
                    icon={MapPin}
                    onClick={() => navigate('/tables')}
                />
                <KPICard
                    title="Cuisine"
                    value={kpis?.cuisine_tickets || 0}
                    subtitle={kpis?.cuisine_tickets > 0 ? "Tickets en cours" : "Aucun ticket"}
                    icon={ChefHat}
                    alert={kpis?.cuisine_tickets > 5}
                    onClick={() => navigate('/kitchen-dashboard')}
                />
                <KPICard
                    title="Bar"
                    value={kpis?.bar_tickets || 0}
                    subtitle={kpis?.bar_tickets > 0 ? "Tickets en cours" : "Aucun ticket"}
                    icon={GlassWater}
                    alert={kpis?.bar_tickets > 5}
                    onClick={() => navigate('/bar-dashboard')}
                />
            </div>

            {/* ============================================ */}
            {/* MODULE PRÉSENCES EN TEMPS RÉEL (Managers uniquement) */}
            {/* ============================================ */}
            {isManager && (
                <StaffPresenceModule compact={true} />
            )}

            {/* ============================================ */}
            {/* ACCÈS RAPIDE */}
            {/* ============================================ */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Accès rapide</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <QuickActionButton
                        to="/pos"
                        icon={CreditCard}
                        label="Caisse"
                        sublabel="POS"
                        colorClass="bg-gradient-to-br from-emerald-500 to-emerald-600"
                    />
                    <QuickActionButton
                        to="/tables"
                        icon={MapPin}
                        label="Tables"
                        sublabel="Gestion salle"
                        colorClass="bg-gradient-to-br from-blue-500 to-blue-600"
                    />
                    <QuickActionButton
                        to="/kitchen-dashboard"
                        icon={ChefHat}
                        label="Cuisine"
                        sublabel="Tickets"
                        colorClass="bg-gradient-to-br from-orange-500 to-orange-600"
                    />
                    <QuickActionButton
                        to="/bar-dashboard"
                        icon={GlassWater}
                        label="Bar"
                        sublabel="Tickets"
                        colorClass="bg-gradient-to-br from-purple-500 to-purple-600"
                    />
                    <QuickActionButton
                        to="/team-chat"
                        icon={MessageSquare}
                        label="Messagerie"
                        sublabel="Chat équipe"
                        colorClass="bg-gradient-to-br from-indigo-500 to-indigo-600"
                    />
                    <QuickActionButton
                        to="/inventory"
                        icon={Warehouse}
                        label="Stock"
                        sublabel="Inventaire"
                        colorClass="bg-gradient-to-br from-teal-500 to-teal-600"
                    />
                    <QuickActionButton
                        to="/reservations"
                        icon={CalendarCheck}
                        label="Réservations"
                        sublabel="Planning"
                        colorClass="bg-gradient-to-br from-pink-500 to-pink-600"
                    />
                </div>
            </div>

            {/* ============================================ */}
            {/* DETAILS - 2 COLONNES */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Produits en stock bas */}
                <Card className="border-0 shadow-md">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-orange-500" />
                                Produits en stock bas
                            </CardTitle>
                            {lowStockProducts.length > 0 && (
                                <Badge variant="destructive">{lowStockProducts.length}</Badge>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {lowStockProducts.length === 0 ? (
                            <EmptyState message="Aucun produit en stock bas" icon={Package} />
                        ) : (
                            <ScrollArea className="h-[200px]">
                                <div className="space-y-2">
                                    {lowStockProducts.map((product, index) => (
                                        <div 
                                            key={product.id || index}
                                            className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                                        >
                                            <div>
                                                <p className="font-medium text-sm">{product.name}</p>
                                                <p className="text-xs text-gray-500">{product.category || 'Non catégorisé'}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-orange-600">
                                                    {product.stock_quantity} {product.unit_base || 'unités'}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Min: {product.min_stock_alert}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                        {lowStockProducts.length > 0 && (
                            <Button 
                                variant="outline" 
                                className="w-full mt-3"
                                onClick={() => navigate('/stock-alerts')}
                            >
                                Voir tous les produits
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {/* Activité récente */}
                <Card className="border-0 shadow-md">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Clock className="w-4 h-4 text-emerald-500" />
                            Activité récente
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {recentActivity.length === 0 ? (
                            <EmptyState message="Aucune activité aujourd'hui" icon={Clock} />
                        ) : (
                            <ScrollArea className="h-[200px]">
                                <div className="space-y-2">
                                    {recentActivity.map((activity, index) => (
                                        <div 
                                            key={index}
                                            className="flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                                                    <DollarSign className="w-4 h-4 text-emerald-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-sm">Paiement validé</p>
                                                    <p className="text-xs text-gray-500">
                                                        Table {activity.table} • {activity.server}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-emerald-600">
                                                    {activity.amount_formatted}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default DashboardPage;
